@let loading = (loading$ | async)!;
@let company = company$ | async;
@let user = user$ | async;
<form class="form" [formGroup]="formGroup" (ngSubmit)="submitForm()">
  <p-card>
    <ng-template #title>
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-6">
          <p-breadcrumb
            [model]="[
              {
                icon: 'fa fa-id-card  fa-fw',
                label: 'sidebar.companyProfile' | translate,
                routerLink: '/admin/companies',
              },
              { label: $safeNavigationMigration(company?.naam) },
            ]"
          ></p-breadcrumb>
        </div>
        <div class="col-span-6 flex items-center justify-end gap-2">
          <button
            pButton
            severity="success"
            type="submit"
            [disabled]="
              !formGroup.valid ||
              !reportColorFormGroup.valid ||
              !hasPermission('write', globalRolePermissionsParams(user, 'companyProfile'))
            "
          >
            <fa-icon [icon]="faSave"></fa-icon>
            <span>{{ 'buttons.saveSingle' | translate }}</span>
          </button>
        </div>
      </div>
    </ng-template>

    <div class="relative">
      @if (loading) {
        <div class="absolute inset-0 z-10 flex items-center justify-center">
          <div class="bg-surface-0 absolute inset-0 opacity-60"></div>
          <p-progress-spinner class="relative"></p-progress-spinner>
        </div>
      }
      <div class="grid grid-cols-12 gap-6">
        <div class="col-span-12 flex flex-col gap-5 pt-2 lg:col-span-7">
          <div>
            @let fieldName = 'naam';
            <p-floatlabel variant="on">
              <input
                [id]="fieldName"
                pInputText
                type="text"
                [formControlName]="fieldName"
                [invalid]="isInvalid(formGroup.controls[fieldName])"
              />
              <label [htmlFor]="fieldName">
                {{ 'fields.name' | translate }}
                <app-required-marker />
              </label>
              <app-form-error [control]="formGroup.controls[fieldName]" />
            </p-floatlabel>
          </div>
          <div>
            @let fieldAddress = 'address';
            <p-floatlabel variant="on">
              <input
                [id]="fieldAddress"
                pInputText
                type="text"
                [formControlName]="fieldAddress"
                [invalid]="isInvalid(formGroup.controls[fieldAddress])"
              />
              <label [htmlFor]="fieldAddress">
                {{ 'fields.address' | translate }}
              </label>
              <app-form-error [control]="formGroup.controls[fieldAddress]" />
            </p-floatlabel>
          </div>
          <div class="grid grid-cols-12 gap-2">
            <div class="col-span-12 md:col-span-3">
              @let fieldPostcode = 'postcode';
              <p-floatlabel variant="on">
                <input
                  [id]="fieldPostcode"
                  pInputText
                  type="text"
                  [formControlName]="fieldPostcode"
                  [invalid]="isInvalid(formGroup.controls[fieldPostcode])"
                />
                <label [htmlFor]="fieldPostcode">
                  {{ 'fields.postalCode' | translate }}
                </label>
                <app-form-error [control]="formGroup.controls[fieldPostcode]" />
              </p-floatlabel>
            </div>
            <div class="col-span-12 md:col-span-9">
              @let fieldPlace = 'place';
              <p-floatlabel variant="on">
                <input
                  [id]="fieldPlace"
                  pInputText
                  type="text"
                  [formControlName]="fieldPlace"
                  [invalid]="isInvalid(formGroup.controls[fieldPlace])"
                />
                <label [htmlFor]="fieldPlace">
                  {{ 'fields.place' | translate }}
                </label>
                <app-form-error [control]="formGroup.controls[fieldPlace]" />
              </p-floatlabel>
            </div>
          </div>
          <div>
            @let fieldTlf = 'tlf';
            <p-floatlabel variant="on">
              <input
                [id]="fieldTlf"
                pInputText
                type="text"
                [formControlName]="fieldTlf"
                [invalid]="isInvalid(formGroup.controls[fieldTlf])"
              />
              <label [htmlFor]="fieldTlf">
                {{ 'fields.telephone' | translate }}
              </label>
              <app-form-error [control]="formGroup.controls[fieldTlf]" />
            </p-floatlabel>
          </div>
          <div>
            @let fieldEmail = 'email';
            <p-floatlabel variant="on">
              <input
                [id]="fieldEmail"
                pInputText
                type="text"
                [formControlName]="fieldEmail"
                [invalid]="isInvalid(formGroup.controls[fieldEmail])"
              />
              <label [htmlFor]="fieldEmail">
                {{ 'fields.email' | translate }}
              </label>
              <app-form-error [control]="formGroup.controls[fieldEmail]" />
            </p-floatlabel>
          </div>
          <div>
            @let fieldWebsite = 'website';
            <p-floatlabel variant="on">
              <input
                [id]="fieldWebsite"
                pInputText
                type="text"
                [formControlName]="fieldWebsite"
                [invalid]="isInvalid(formGroup.controls[fieldWebsite])"
              />
              <label [htmlFor]="fieldWebsite">
                {{ 'fields.website' | translate }}
              </label>
              <app-form-error [control]="formGroup.controls[fieldWebsite]" />
            </p-floatlabel>
          </div>
        </div>
        <div class="col-span-12 lg:col-span-5">
          <div class="company-logo flex h-full flex-col gap-3 pt-2">
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div class="text-surface-700 flex items-center gap-2">
                <fa-icon [icon]="faImage"></fa-icon>
                <span class="text-md font-medium">{{ 'companyProfile.companyLogo.title' | translate }}</span>
              </div>
              <div class="flex items-center gap-2">
                <p-fileupload
                  #fileUpload
                  [disabled]="
                    formGroup.disabled ||
                    loading ||
                    loadingLogo() ||
                    uploadingLogo() ||
                    deletingLogo() ||
                    !company ||
                    !company.id
                  "
                  name="logo"
                  [auto]="true"
                  [customUpload]="true"
                  mode="basic"
                  accept="image/png,image/jpg,image/jpeg"
                  [chooseLabel]="'upload' | translate"
                  (uploadHandler)="uploadLogo($event, fileUpload)"
                >
                  <ng-template #chooseicon>
                    @if (uploadingLogo()) {
                      <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
                    } @else {
                      <fa-icon [icon]="faUpload"></fa-icon>
                    }
                  </ng-template>
                </p-fileupload>
                @if (imageSrc) {
                  <button
                    pButton
                    severity="secondary"
                    type="button"
                    [outlined]="true"
                    (click)="downloadLogo()"
                    [disabled]="loadingLogo() || uploadingLogo() || deletingLogo()"
                  >
                    <fa-icon [icon]="faDownload"></fa-icon>
                    <span>{{ 'buttons.download' | translate }}</span>
                  </button>
                  <button
                    pButton
                    severity="danger"
                    type="button"
                    [outlined]="true"
                    (click)="deleteLogo(fileUpload)"
                    [disabled]="formGroup.disabled || loadingLogo() || uploadingLogo() || deletingLogo()"
                  >
                    @if (deletingLogo()) {
                      <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
                    } @else {
                      <fa-icon [icon]="faTrash"></fa-icon>
                    }
                    <span>{{ 'buttons.deleteSingle' | translate }}</span>
                  </button>
                }
              </div>
            </div>
            <div
              class="logo-image-container border-surface-300 bg-surface-0 relative flex min-h-40 w-full flex-1 items-center justify-center overflow-hidden rounded-lg border shadow-xs"
              [class.border-dashed]="!imageSrc"
            >
              @if (imageSrc) {
                <img [src]="imageSrc" alt="logo" class="absolute inset-0 h-full w-full object-contain p-3" />
              } @else {
                <div class="text-surface-400 flex flex-col items-center gap-2 p-4 text-center">
                  <fa-icon [icon]="faImage" class="text-4xl opacity-70"></fa-icon>
                  <span class="text-xs">{{ 'companyProfile.companyLogo.empty' | translate }}</span>
                </div>
              }

              @if (loadingLogo()) {
                <div class="bg-surface-0 absolute inset-0 flex items-center justify-center">
                  <fa-icon [icon]="faSpinner" animation="spin" size="2xl"></fa-icon>
                </div>
              } @else if (uploadingLogo() || deletingLogo()) {
                <div class="bg-surface-0 absolute inset-0 opacity-50"></div>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  </p-card>

  @if (userRoles | includes: 'admin') {
    <p-card class="mt-6">
      <div class="grid grid-cols-12 gap-4">
        <div class="col-span-6">
          <div class="col-span-12 mt-8 p-0">
            @let fieldLicense = 'licentie';
            <p-floatlabel variant="on">
              <input
                [id]="fieldLicense"
                pInputText
                type="text"
                [formControlName]="fieldLicense"
                [invalid]="isInvalid(formGroup.controls[fieldLicense])"
              />
              <label [htmlFor]="fieldLicense">
                {{ 'fields.license' | translate }}
                <app-required-marker />
              </label>
              <app-form-error [control]="formGroup.controls[fieldLicense]" />
            </p-floatlabel>
          </div>
          <div class="col-span-12 mt-8 p-0">
            @let fieldIsClient = 'isopdrachtgever';
            <span class="flex items-center gap-2">
              <span>
                {{ 'fields.isClient' | translate }}
              </span>
              <p-toggle-switch
                [formControlName]="fieldIsClient"
                [inputId]="fieldIsClient"
                [id]="fieldIsClient"
                class="mt-2"
              ></p-toggle-switch>
            </span>
          </div>
        </div>
        <div class="col-span-2">
          <h3 class="m-0 p-0">{{ 'fields.modules' | translate }}</h3>
          <div class="flex flex-col gap-2">
            @for (module of modules; track module) {
              <div class="flex gap-2">
                <p-checkbox
                  name="group"
                  [value]="module.value"
                  formControlName="modules"
                  [inputId]="module.value"
                ></p-checkbox>
                <label [for]="module.value">
                  <fa-icon [icon]="module.icon" size="xl" [style]="{ color: module.color }"></fa-icon>
                  {{ module.name }}
                </label>
              </div>
            }
          </div>
        </div>
        <div class="col-span-2">
          <h3 class="m-0 p-0">{{ 'fields.categories' | translate }}</h3>
          <div class="flex flex-col gap-2">
            @for (category of categories; track category) {
              <div class="flex gap-2">
                <p-checkbox
                  name="group"
                  [value]="category.value"
                  formControlName="categorien"
                  [inputId]="category.value"
                ></p-checkbox>
                <label [for]="category.value">
                  <fa-icon [icon]="category.icon" size="xl"></fa-icon>
                  {{ category.name }}
                </label>
              </div>
            }
          </div>
        </div>
        <div class="col-span-2">
          <h3 class="m-0 p-0">{{ 'fields.modules' | translate }}</h3>
          <div class="flex flex-col gap-2">
            @for (module of extraModules; track module) {
              <div class="flex gap-2">
                <p-checkbox
                  name="group"
                  [value]="module.value"
                  formControlName="modules"
                  [inputId]="module.value"
                ></p-checkbox>
                <label [for]="module.value">
                  <fa-icon [icon]="module.icon" size="xl" [style]="{ color: module.color }"></fa-icon>
                  {{ module.name }}
                </label>
              </div>
            }
          </div>
        </div>
      </div>
    </p-card>
  }
</form>

@if (company && company.id) {
  <p-card class="mt-6">
    <ng-template #title>
      <div class="flex">
        {{ 'contactPersons' | translate }}
        <div class="grow"></div>
        <button
          pButton
          pRipple
          severity="success"
          type="submit"
          (click)="addNewItem()"
          [disabled]="
            (mode === 'company-profile' &&
              !hasPermission('write', globalRolePermissionsParams(user, 'companyProfile'))) ||
            loading
          "
        >
          <fa-icon [icon]="faPlus"></fa-icon>
          <span>{{ 'add' | translate }}</span>
        </button>
      </div>
    </ng-template>
    <app-agnostic-grids
      [dependencies]="gridDependencies"
      (gridApiChange)="onPersonGridApiChange($event)"
      [loading]="loading"
    ></app-agnostic-grids>
  </p-card>
  <p-card class="mt-6">
    <ng-template #title>
      <div class="flex justify-between">
        <div class="flex items-center gap-2 pb-2">
          <fa-icon [icon]="faGear" size="lg"></fa-icon>
          <span class="text-2xl font-semibold">{{ 'companyProfile.reportSettings' | translate }}</span>
        </div>
      </div>
    </ng-template>

    <div class="grid grid-cols-1 gap-5 lg:grid-cols-2">
      <section class="border-surface-200 bg-surface-50 flex flex-col rounded-xl border p-5">
        <header class="text-surface-700 mb-4 flex items-center gap-2">
          <fa-icon [icon]="faPalette"></fa-icon>
          <span class="text-xl font-semibold">{{ 'companyProfile.reportColorsSection' | translate }}</span>
        </header>

        <form [formGroup]="reportColorFormGroup" class="flex flex-col gap-3">
          <div
            class="border-surface-200 bg-surface-0 flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5"
          >
            <label for="title" class="text-surface-700 text-sm font-medium">
              {{ 'companyProfile.reportSettingTitle' | translate }}
            </label>
            <div class="flex items-center gap-2">
              <button
                type="button"
                id="title"
                aria-label="{{ 'companyProfile.reportSettingTitle' | translate }}"
                class="border-surface-300 h-6 w-6 rounded border"
                [style.background]="reportColorFormGroup.controls.titleColor.value"
                (click)="titleColorPopover.toggle($event)"
              ></button>
              <p-popover #titleColorPopover>
                <p-inputcolor formControlName="titleColor" format="hexa" class="flex w-52 flex-col gap-3">
                  <p-inputcolor-area>
                    <p-inputcolor-area-background />
                    <p-inputcolor-area-handle />
                  </p-inputcolor-area>
                  <p-inputcolor-slider>
                    <p-inputcolor-slider-track />
                    <p-inputcolor-slider-handle />
                  </p-inputcolor-slider>
                  <div class="flex items-center gap-2">
                    <p-inputcolor-swatch class="h-8 w-8 shrink-0">
                      <p-inputcolor-swatch-background />
                    </p-inputcolor-swatch>
                    <input
                      pInputColorInput
                      channel="hex"
                      pInputText
                      fluid
                      class="uppercase"
                      [aria-label]="'companyProfile.reportSettingTitle' | translate"
                    />
                  </div>
                </p-inputcolor>
              </p-popover>
              <input
                pInputText
                type="text"
                pSize="small"
                formControlName="titleColor"
                [aria-label]="'companyProfile.reportSettingTitle' | translate"
                class="w-28 text-center font-mono text-sm uppercase"
                [invalid]="isInvalid(reportColorFormGroup.controls.titleColor)"
              />
            </div>
          </div>

          <div
            class="border-surface-200 bg-surface-0 flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5"
          >
            <label for="subtitle" class="text-surface-700 text-sm font-medium">
              {{ 'companyProfile.reportSettingSubtitle' | translate }}
            </label>
            <div class="flex items-center gap-2">
              <button
                type="button"
                id="subtitle"
                aria-label="{{ 'companyProfile.reportSettingSubtitle' | translate }}"
                class="border-surface-300 h-6 w-6 rounded border"
                [style.background]="reportColorFormGroup.controls.subtitleColor.value"
                (click)="subtitleColorPopover.toggle($event)"
              ></button>
              <p-popover #subtitleColorPopover>
                <p-inputcolor formControlName="subtitleColor" format="hexa" class="flex w-52 flex-col gap-3">
                  <p-inputcolor-area>
                    <p-inputcolor-area-background />
                    <p-inputcolor-area-handle />
                  </p-inputcolor-area>
                  <p-inputcolor-slider>
                    <p-inputcolor-slider-track />
                    <p-inputcolor-slider-handle />
                  </p-inputcolor-slider>
                  <div class="flex items-center gap-2">
                    <p-inputcolor-swatch class="h-8 w-8 shrink-0">
                      <p-inputcolor-swatch-background />
                    </p-inputcolor-swatch>
                    <input
                      pInputColorInput
                      channel="hex"
                      pInputText
                      fluid
                      class="uppercase"
                      aria-label="{{ 'companyProfile.reportSettingSubtitle' | translate }}"
                    />
                  </div>
                </p-inputcolor>
              </p-popover>
              <input
                pInputText
                type="text"
                pSize="small"
                formControlName="subtitleColor"
                aria-label="{{ 'companyProfile.reportSettingSubtitle' | translate }}"
                class="w-28 text-center font-mono text-sm uppercase"
                [invalid]="isInvalid(reportColorFormGroup.controls.subtitleColor)"
              />
            </div>
          </div>

          <div
            class="border-surface-200 bg-surface-0 flex items-center justify-between gap-3 rounded-lg border px-3 py-2.5"
          >
            <label for="companyPageText" class="text-surface-700 text-sm font-medium">
              {{ 'companyProfile.reportSettingCompanyPageText' | translate }}
            </label>
            <div class="flex items-center gap-2">
              <button
                type="button"
                id="companyPageText"
                aria-label="{{ 'companyProfile.reportSettingCompanyPageText' | translate }}"
                class="border-surface-300 h-6 w-6 rounded border"
                [style.background]="reportColorFormGroup.controls.companyPageTextColor.value"
                (click)="companyPageTextColorPopover.toggle($event)"
              ></button>
              <p-popover #companyPageTextColorPopover>
                <p-inputcolor formControlName="companyPageTextColor" format="hexa" class="flex w-52 flex-col gap-3">
                  <p-inputcolor-area>
                    <p-inputcolor-area-background />
                    <p-inputcolor-area-handle />
                  </p-inputcolor-area>
                  <p-inputcolor-slider>
                    <p-inputcolor-slider-track />
                    <p-inputcolor-slider-handle />
                  </p-inputcolor-slider>
                  <div class="flex items-center gap-2">
                    <p-inputcolor-swatch class="h-8 w-8 shrink-0">
                      <p-inputcolor-swatch-background />
                    </p-inputcolor-swatch>
                    <input
                      pInputColorInput
                      channel="hex"
                      pInputText
                      fluid
                      class="uppercase"
                      aria-label="{{ 'companyProfile.reportSettingCompanyPageText' | translate }}"
                    />
                  </div>
                </p-inputcolor>
              </p-popover>
              <input
                pInputText
                type="text"
                pSize="small"
                formControlName="companyPageTextColor"
                aria-label="{{ 'companyProfile.reportSettingCompanyPageText' | translate }}"
                class="w-28 text-center font-mono text-sm uppercase"
                [invalid]="isInvalid(reportColorFormGroup.controls.companyPageTextColor)"
              />
            </div>
          </div>
        </form>

        <div class="mt-auto flex justify-end pt-4">
          <button pButton type="button" severity="secondary" variant="outlined" (click)="resetColors()">
            <fa-icon [icon]="faUndo"></fa-icon>
            <span>{{ 'buttons.reset' | translate }}</span>
          </button>
        </div>
      </section>

      <section class="border-surface-200 bg-surface-50 flex flex-col rounded-xl border p-5">
        <header class="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div class="text-surface-700 flex items-center gap-2">
            <fa-icon [icon]="faImage"></fa-icon>
            <span class="text-xl font-semibold">{{ 'companyProfile.companyBackground.title' | translate }}</span>
          </div>
          <div class="flex items-center gap-2">
            <p-fileupload
              #bgUpload
              [disabled]="
                formGroup.disabled ||
                loading ||
                !company ||
                !company.id ||
                loadingCompanyBackground() ||
                uploadingCompanyBackground() ||
                deletingCompanyBackground()
              "
              name="companyBackgroundImage"
              [auto]="true"
              [customUpload]="true"
              mode="basic"
              accept="image/png,image/jpg,image/jpeg"
              [chooseLabel]="'upload' | translate"
              (uploadHandler)="uploadCompanyBackground($event, bgUpload)"
            >
              <ng-template #chooseicon>
                @if (uploadingCompanyBackground()) {
                  <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
                } @else {
                  <fa-icon [icon]="faUpload"></fa-icon>
                }
              </ng-template>
            </p-fileupload>

            @if (companyBackgroundImageSrc()) {
              <button
                pButton
                severity="danger"
                type="button"
                [outlined]="true"
                (click)="deleteCompanyBackground()"
                [disabled]="
                  formGroup.disabled ||
                  loadingCompanyBackground() ||
                  uploadingCompanyBackground() ||
                  deletingCompanyBackground()
                "
              >
                @if (deletingCompanyBackground()) {
                  <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
                } @else {
                  <fa-icon [icon]="faTrash"></fa-icon>
                }
                <span>{{ 'buttons.deleteSingle' | translate }}</span>
              </button>
            }
          </div>
        </header>

        <div class="flex flex-col gap-5 sm:flex-row sm:items-stretch">
          <div
            class="border-surface-300 bg-surface-0 relative flex aspect-210/297 w-36 shrink-0 items-center justify-center self-center overflow-hidden rounded-lg border shadow-xs sm:self-start"
            [class.border-dashed]="!companyBackgroundImageSrc()"
          >
            @if (companyBackgroundImageSrc(); as bgSrc) {
              <img [src]="bgSrc" alt="A4" class="h-full w-full object-contain" />
            } @else {
              <div class="text-surface-400 flex flex-col items-center gap-2 p-4 text-center">
                <fa-icon [icon]="faImage" class="text-4xl opacity-70"></fa-icon>
                <span class="text-xs">{{ 'companyProfile.companyBackground.empty' | translate }}</span>
              </div>
            }

            @if (loadingCompanyBackground()) {
              <div class="bg-surface-0 absolute inset-0 flex items-center justify-center">
                <fa-icon [icon]="faSpinner" animation="spin" size="2xl"></fa-icon>
              </div>
            } @else if (uploadingCompanyBackground() || deletingCompanyBackground()) {
              <div class="bg-surface-0 absolute inset-0 opacity-50"></div>
            }
          </div>

          <div class="flex flex-1 flex-col gap-2">
            <p class="text-surface-600 text-md m-0">
              {{ 'companyProfile.companyBackground.description' | translate }}
            </p>
            <p class="text-surface-600 bg-surface-100 m-0 flex items-start gap-2 rounded-md px-2.5 py-2 text-sm">
              <fa-icon [icon]="faImage" class="mt-0.5"></fa-icon>
              <span>
                {{
                  'companyProfile.companyBackground.a4Hint'
                    | translate
                      : {
                          optimalWidth: A4_OPTIMAL_WIDTH,
                          optimalHeight: A4_OPTIMAL_HEIGHT,
                          minWidth: A4_MIN_WIDTH_PX,
                          minHeight: A4_MIN_HEIGHT_PX,
                        }
                }}
              </span>
            </p>

            @if (companyBackgroundImageSrc()) {
              <form [formGroup]="formGroup" class="mt-auto pt-2">
                @let fieldCompanyPageTextDisabled = 'companyPageTextDisabled';
                <div class="flex items-center gap-2">
                  <p-toggle-switch
                    [formControlName]="fieldCompanyPageTextDisabled"
                    [inputId]="fieldCompanyPageTextDisabled"
                  ></p-toggle-switch>
                  <label [for]="fieldCompanyPageTextDisabled" class="text-surface-700 text-sm font-medium">
                    {{ 'companyProfile.companyBackground.textDisabled' | translate }}
                  </label>
                </div>
                <p class="text-surface-500 m-0 mt-1 text-xs">
                  {{ 'companyProfile.companyBackground.textDisabledHint' | translate }}
                </p>
              </form>
            }
          </div>
        </div>
      </section>
    </div>
  </p-card>

  <p-card class="mt-6">
    <ng-template #title>
      <div class="flex items-center gap-2 pb-2">
        <fa-icon [icon]="faPlug" size="lg"></fa-icon>
        <span class="text-2xl font-semibold">{{ 'companyProfile.integrations' | translate }}</span>
      </div>
    </ng-template>

    <form [formGroup]="formGroup">
      <div class="flex flex-col gap-5">
        <section class="border-surface-200 bg-surface-50 rounded-xl border p-5">
          <header class="border-surface-200 mb-4 flex items-center justify-between gap-2 border-b pb-3">
            <span class="text-surface-700 text-xl font-semibold">
              {{ 'companyProfile.afasApiSettings' | translate }}
            </span>
            <img alt="AFAS" src="assets/images/integrations/afas.png" class="max-h-8" />
          </header>
          <div>
            <div class="grid grid-cols-12 items-center gap-4">
              <div class="col-span-12 md:col-span-6">
                <p-floatlabel variant="on">
                  <p-iconfield class="w-full">
                    <input
                      pInputPassword
                      id="afasToken"
                      formControlName="afasToken"
                      [(mask)]="afasTokenMask"
                      class="w-full"
                      [invalid]="isInvalid(formGroup.controls['afasToken'])"
                    />
                    <p-inputicon>
                      <button
                        type="button"
                        class="cursor-pointer border-0 bg-transparent p-0"
                        [attr.aria-label]="
                          (afasTokenMask() ? 'buttons.showPassword' : 'buttons.hidePassword') | translate
                        "
                        (click)="afasTokenMask.set(!afasTokenMask())"
                      >
                        <fa-icon [icon]="afasTokenMask() ? faEye : faEyeSlash"></fa-icon>
                      </button>
                    </p-inputicon>
                  </p-iconfield>
                  <label for="afasToken">Afas token</label>
                </p-floatlabel>
              </div>
              <div class="col-span-12 md:col-span-3">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="afasId"
                    type="text"
                    formControlName="afasId"
                    [invalid]="isInvalid(formGroup.controls['afasId'])"
                  />
                  <label for="afasId">Afas admin ID</label>
                </p-floatlabel>
              </div>
              <div class="col-span-12 flex items-center gap-2 md:col-span-3">
                <p-checkbox
                  [binary]="true"
                  formControlName="uploadReportsIntegration"
                  inputId="uploadReportsIntegration"
                ></p-checkbox>
                <label for="uploadReportsIntegration">
                  {{ 'companyProfile.afasUploadInspections' | translate }}
                </label>
              </div>
            </div>
          </div>
        </section>

        <section class="border-surface-200 bg-surface-50 rounded-xl border p-5">
          <header class="border-surface-200 mb-4 flex items-center justify-between gap-2 border-b pb-3">
            <span class="text-surface-700 text-xl font-semibold">
              {{ 'companyProfile.exactApiSettings' | translate }}
            </span>
            <img alt="EXACT" src="assets/images/integrations/exact.webp" class="max-h-8" />
          </header>
          <div>
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-12 md:col-span-6">
                <p-floatlabel variant="on">
                  <p-iconfield class="w-full">
                    <input
                      pInputPassword
                      id="exactApiKey"
                      formControlName="exactApiKey"
                      [(mask)]="exactApiKeyMask"
                      class="w-full"
                      [invalid]="isInvalid(formGroup.controls['exactApiKey'])"
                    />
                    <p-inputicon>
                      <button
                        type="button"
                        class="cursor-pointer border-0 bg-transparent p-0"
                        [attr.aria-label]="
                          (exactApiKeyMask() ? 'buttons.showPassword' : 'buttons.hidePassword') | translate
                        "
                        (click)="exactApiKeyMask.set(!exactApiKeyMask())"
                      >
                        <fa-icon [icon]="exactApiKeyMask() ? faEye : faEyeSlash"></fa-icon>
                      </button>
                    </p-inputicon>
                  </p-iconfield>
                  <label for="exactApiKey">Exact API key</label>
                </p-floatlabel>
              </div>
              <div class="col-span-12 md:col-span-6">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="exactUrl"
                    type="text"
                    formControlName="exactUrl"
                    [invalid]="isInvalid(formGroup.controls['exactUrl'])"
                  />
                  <label for="exactUrl">Exact url</label>
                </p-floatlabel>
              </div>
            </div>
          </div>
        </section>

        <section class="border-surface-200 bg-surface-50 rounded-xl border p-5">
          <header class="border-surface-200 mb-4 flex items-center justify-between gap-2 border-b pb-3">
            <span class="text-surface-700 text-xl font-semibold">{{ 'companyProfile.itrisSettings' | translate }}</span>
            <img alt="ITRIS" src="assets/images/integrations/itris.png" class="max-h-8" />
          </header>
          <div>
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-12 md:col-span-6">
                <p-floatlabel variant="on">
                  <input
                    pInputText
                    id="itrisUsername"
                    type="text"
                    formControlName="itrisUsername"
                    [invalid]="isInvalid(formGroup.controls['itrisUsername'])"
                  />
                  <label for="itrisUsername">Itris username</label>
                </p-floatlabel>
              </div>
              <div class="col-span-12 md:col-span-6">
                <p-floatlabel variant="on">
                  <p-iconfield class="w-full">
                    <input
                      pInputPassword
                      id="itrisPassword"
                      formControlName="itrisPassword"
                      [(mask)]="itrisPasswordMask"
                      class="w-full"
                      [invalid]="isInvalid(formGroup.controls['itrisPassword'])"
                    />
                    <p-inputicon>
                      <button
                        type="button"
                        class="cursor-pointer border-0 bg-transparent p-0"
                        [attr.aria-label]="
                          (itrisPasswordMask() ? 'buttons.showPassword' : 'buttons.hidePassword') | translate
                        "
                        (click)="itrisPasswordMask.set(!itrisPasswordMask())"
                      >
                        <fa-icon [icon]="itrisPasswordMask() ? faEye : faEyeSlash"></fa-icon>
                      </button>
                    </p-inputicon>
                  </p-iconfield>
                  <label for="itrisPassword">Itris Password</label>
                </p-floatlabel>
              </div>
            </div>
          </div>
        </section>
      </div>
    </form>
  </p-card>
}

<p-dialog
  [(visible)]="contactPersonDialogOpen"
  [style]="{ width: '25vw' }"
  [resizable]="false"
  [modal]="true"
  (onHide)="handleContactPersonHide()"
  [header]="'collections.contactPersons.singular' | translate: {}"
>
  <form [formGroup]="contactPersonFormGroup" (ngSubmit)="submitContactPersonForm()">
    <div class="grid grid-cols-12 gap-4 pt-1">
      <div class="col-span-12">
        @let fieldFirstName = 'voornaam';
        <p-floatlabel variant="on">
          <input
            [id]="fieldFirstName"
            pInputText
            type="text"
            [formControlName]="fieldFirstName"
            [invalid]="isInvalid(contactPersonFormGroup.controls[fieldFirstName])"
          />
          <label [htmlFor]="fieldFirstName">
            {{ 'fields.firstName' | translate }}
            <app-required-marker />
          </label>
          <app-form-error [control]="contactPersonFormGroup.controls[fieldFirstName]" />
        </p-floatlabel>
      </div>
      <div class="col-span-12">
        @let fieldLastName = 'achternaam';
        <p-floatlabel variant="on">
          <input
            [id]="fieldLastName"
            pInputText
            type="text"
            [formControlName]="fieldLastName"
            [invalid]="isInvalid(contactPersonFormGroup.controls[fieldLastName])"
          />
          <label [htmlFor]="fieldLastName">
            {{ 'fields.lastName' | translate }}
            <app-required-marker />
          </label>
          <app-form-error [control]="contactPersonFormGroup.controls[fieldLastName]" />
        </p-floatlabel>
      </div>
      <div class="col-span-12">
        @let fieldFunction = 'functie';
        <p-floatlabel variant="on">
          <input
            [id]="fieldFunction"
            pInputText
            type="text"
            [formControlName]="fieldFunction"
            [invalid]="isInvalid(contactPersonFormGroup.controls[fieldFunction])"
          />
          <label [htmlFor]="fieldFunction">
            {{ 'fields.function' | translate }}
          </label>
          <app-form-error [control]="contactPersonFormGroup.controls[fieldFunction]" />
        </p-floatlabel>
      </div>
      <div class="col-span-12">
        @let fieldMobile = 'mobiel';
        <p-floatlabel variant="on">
          <input
            [id]="fieldMobile"
            pInputText
            type="text"
            [formControlName]="fieldMobile"
            [invalid]="isInvalid(contactPersonFormGroup.controls[fieldMobile])"
          />
          <label [htmlFor]="fieldMobile">
            {{ 'fields.mobile' | translate }}
          </label>
          <app-form-error [control]="contactPersonFormGroup.controls[fieldMobile]" />
        </p-floatlabel>
      </div>
      <div class="col-span-12">
        @let fieldContactEmail = 'email';
        <p-floatlabel variant="on">
          <input
            [id]="fieldContactEmail"
            pInputText
            type="text"
            [formControlName]="fieldContactEmail"
            [invalid]="isInvalid(contactPersonFormGroup.controls[fieldContactEmail])"
          />
          <label [htmlFor]="fieldContactEmail">
            {{ 'fields.email' | translate }}
          </label>
          <app-form-error [control]="contactPersonFormGroup.controls[fieldContactEmail]" />
        </p-floatlabel>
      </div>
    </div>
    <div class="col-span-12 mt-4 flex justify-between gap-4 p-0">
      <button
        pButton
        type="button"
        severity="secondary"
        (click)="handleContactPersonHide()"
        [disabled]="savingContactPerson()"
      >
        <fa-icon [icon]="faTimes"></fa-icon>
        <span>{{ 'buttons.cancel' | translate }}</span>
      </button>
      <button pButton type="submit" severity="success" [disabled]="isSaveButtonDisabled() || savingContactPerson()">
        @if (savingContactPerson()) {
          <fa-icon [icon]="faSpinner" animation="spin"></fa-icon>
        } @else {
          <fa-icon [icon]="faSave"></fa-icon>
        }
        <span>{{ 'buttons.saveSingle' | translate }}</span>
      </button>
    </div>
  </form>
</p-dialog>
